import type { CSSProperties, ReactNode } from 'react';
import { V2CloseButton } from '@sakhtar/design-system/react';

export interface PopoverAnchorProps {
  trigger: ReactNode;
  open: boolean;
  /** RTL-aware: 'start' = inset-inline-start:0, 'end' = inset-inline-end:0 — matches the board's own anchoring per popover. */
  align: 'start' | 'end';
  width: number;
  /** Phones have no Esc and the panel covers the page: a visible close button calls this. */
  onClose?: () => void;
  children: ReactNode;
}

const panelBase: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 10px)',
  background: 'var(--sk-bg-elev)',
  border: '1px solid var(--sk-border)',
  borderRadius: 14,
  boxShadow: 'var(--sk-shadow-3)',
  // Shell stacking audit: was a bare 60, disconnected from the shared
  // --sk-z-* scale (packages/design-system/src/tokens/v2/spacing.ts) and
  // below the shell's own --sk-z-sticky (100) topbar/sidebar — a topbar
  // popover (Quick Add, notifications, …) could render behind sticky page
  // content. --sk-z-modal keeps it comfortably above both the topbar and
  // the mobile bottom nav (--sk-z-overlay) even when its own maxHeight
  // reaches the bottom of a short mobile viewport.
  zIndex: 'var(--sk-z-modal)',
  animation: 'sk-pop-in var(--sk-dur-2) var(--sk-ease-standard)',
  maxWidth: 'calc(100vw - 32px)',
  // V2AppShell (Shell.tsx) is a fixed-viewport shell — overflow: hidden on
  // its root, so unlike a scrolling document, content that spills past the
  // viewport here has no scroll path to reach it at all. Every panel is
  // anchored `--sk-topbar-h` + 10px down from the top, so that's the most
  // it can ever have below it; clamping to that (minus a little breathing
  // room) plus its own scroll keeps every popover fully reachable
  // regardless of content length or viewport height (caught by review,
  // not by this session's own local testing — real bug, real fix).
  maxHeight: 'calc(100vh - var(--sk-topbar-h) - 24px)',
  overflowY: 'auto',
};

/**
 * Under the mobile breakpoint a panel anchored to its trigger can't fit:
 * in RTL the search field sits at the screen's left edge, so a panel
 * aligned to its inline-start (right) edge grows leftward off-screen. There
 * every panel is pinned to the viewport instead — full width minus a 16px
 * gutter, just below the topbar. `!important` beats the inline anchoring.
 */
const MOBILE_PANEL_CSS = `@media (max-width: 899px) {
  .sk-topbar-popover {
    position: fixed !important;
    top: calc(var(--sk-topbar-h) + 8px) !important;
    left: 16px !important;
    right: 16px !important;
    width: auto !important;
    max-width: none !important;
    max-height: calc(100dvh - var(--sk-topbar-h) - 16px) !important;
  }
  .sk-topbar-popover__close { display: flex !important; }
}
.sk-topbar-popover__close {
  display: none;
  position: sticky;
  top: 0;
  z-index: 1;
  justify-content: flex-end;
  padding: 6px 8px 0;
  background: var(--sk-bg-elev);
}`;

/**
 * UI_REFERENCE_SOURCE: Sakhtar Topbar Popovers.dc.html — the `[data-popwrap]`
 * trigger+panel wrapper repeated for all six popovers. `usePopoverState`
 * supplies the single-open/outside-click/Esc behavior; this component is
 * only the anchored-panel layout, reused by every popover below.
 */
export function PopoverAnchor({
  trigger,
  open,
  align,
  width,
  onClose,
  children,
}: PopoverAnchorProps) {
  return (
    <div style={{ position: 'relative' }}>
      {trigger}
      {open ? <style>{MOBILE_PANEL_CSS}</style> : null}
      {open ? (
        <div
          className="sk-topbar-popover"
          style={{
            ...panelBase,
            width,
            ...(align === 'start' ? { insetInlineStart: 0 } : { insetInlineEnd: 0 }),
          }}
        >
          {onClose ? (
            <div className="sk-topbar-popover__close">
              <V2CloseButton onClick={onClose} />
            </div>
          ) : null}
          {children}
        </div>
      ) : null}
    </div>
  );
}
